import {
  SavvasSharedUiColumnChartItem,
  SavvasSharedUiColumnChartStyleConfigs,
  SavvasSharedUiColumnChartXAxisOptions,
  SavvasSharedUiColumnChartYAxisOptions,
} from '@savvaslearning/savvas-shared-ui-components/dist-stencil/interfaces/components/savvas-shared-ui-column-chart/savvas-shared-ui-column-chart-interface';
import {
  AxisRange,
  SavvasSharedUiLineAreaChartItem,
  SavvasSharedUiLineAreaChartToolTipOptions,
  SavvasSharedUiLineAreaChartXAxisOptions,
  SavvasSharedUiLineAreaChartYAxisOptions,
} from '@savvaslearning/savvas-shared-ui-components/dist-stencil/interfaces/components/savvas-shared-ui-line-area-chart/savvas-shared-ui-line-area-chart-interface';
import {
  SavvasSharedUiLineChartItem,
  SavvasSharedUiLineChartStyleConfigs,
  SavvasSharedUiLineChartToolTipOptions,
  SavvasSharedUiLineChartXAxisOptions,
  SavvasSharedUiLineChartYAxisOptions,
} from '@savvaslearning/savvas-shared-ui-components/dist-stencil/interfaces/components/savvas-shared-ui-line-chart/savvas-shared-ui-line-chart-interface';
import {
  getGrowthReports_getGrowthReports_growthReports_students as GrowthReportStudents,
  getGrowthReports_getGrowthReports_growthReports_reports as GrowthReports,
  getGrowthReports_getGrowthReports_growthReports_reports_metrics as Metrics,
  getStudentMultiStageReport_getStudentMultiStageReport_overallReportData as OverallReportData,
  getGrowthReports_getGrowthReports_growthReports_placementLevelMinMaxValues as PlacementLevelMinMaxValues,
  getStudentMultiStageReport_getStudentMultiStageReport as StudentMultiStageReport,
  getStudentMultiStageReport_getStudentMultiStageReport_studentReportData as StudentReportData,
} from 'schema/graphql-types';
import { AssessmentReportingType } from '../../multi-stage-growth-report/models/multi-stage-growth-report.model';
import { PlacementLevelCode } from '../../pages/domain-summary/models/domain-summary.constant';

export enum Locale {
  EN = 'en',
  ES = 'es',
}

export enum OverlaySpinner {
  ARIA_LABEL = 'overlay-spinner.loading',
}

export type InputParams = {
  classId: string;
  rootProgramId: string;
  locale?: string;
};

export interface RemoteModuleConfig {
  exposedModule: string;
  moduleName: string;
  remoteEntry: string;
  remoteName: string;
}

export interface MultiPartProgressBarItem {
  id: string;
  percentage: number;
  label: string;
  fillColor: Colors;
}

export enum Colors {
  LIGHTEST_RED = '#FAEBEC',
  RED = '#CC333F',
  RED_HOVER = '#B42934',
  RED_TEXT = '#FFFFFF',
  GREEN = '#35AA35',
  GREEN_HOVER = '#2A9A2A',
  GREEN_TEXT = '#000000',
  YELLOW = '#FFBA4A',
  YELLOW_HOVER = '#E5A439',
  YELLOW_TEXT = '#000000',
  BLUE = '#584CE4',
  BLUE_HOVER = '#372CB4',
  BLUE_TEXT = '#FFFFFF',
  CUSTOM = 'custom',
  CUSTOM_HOVER = 'custom_hover',
  Pinkish = '#BE22A1',
  LightPink = '#F9EBF1',
  DarkGrey = '#333333',
  OffWhite = '#FDFAF7',
  PalePeach = '#FDF1E9',
  Rust = '#DF6411',
  OldLace = '#FDF6EB',
  LightRust = '#C27E0F',
  Honeydew = '#E6F3E6',
  Berry = '#FAF0F8',
  LightGrey = '#F2F2F2',
  BlueMagenta = '#EEEDFC',
  PaleGreen = '#EBF7EB',
  FloralWhite = '#FFF8ED',
}

export enum IconType {
  SolidSquare = 'assets/images/solid-square-legend.svg',
  DottedCircle = 'assets/images/dotted-circle-legend.svg',
}

export enum IconLabelKey {
  SolidSquare = 'solidSquare',
  DottedCircle = 'dottedCircle',
}

export enum GrowthScoreLabels {
  ShowDistrictAverageScore = 'overall-score-summary.show-district-avg-score',
  ShowClassAverage = 'overall-score-summary.show-class-average',
  ShowGrowth = 'overall-score-summary.show-growth',
  ClassAverageScore = 'overall-score-summary.class-average-score',
  DistrictAverageScore = 'overall-score-summary.district-average-score',
  StudentScore = 'overall-score-summary.student-score',
  ClassAverageOverallScoreMessage = 'overall-score-summary.class-average-overall-score-message',
  ClassAverageOverallScoreMOYMessage = 'overall-score-summary.class-average-overall-score-moy-message',
  NoDistrictAverageScoreMessage = 'overall-score-summary.no-district-average-score-message',
  NoDistrictAverageScoreMOYMessage = 'overall-score-summary.no-district-average-score-moy-message',
  NoGrowthScoreMessage = 'overall-score-summary.no-growth-message',
  AssessedStudentsPopoverScaleScore = 'overall-score-summary.assessed-students-popup.scale-score',
  AssessedStudentsPopoverAssigned = 'overall-score-summary.assessed-students-popup.assigned-to-students',
  DropDownValues = 'common.dropdown-values',
}

export enum GrowthMetricLabels {
  NoGrowthMetricMessage = 'overall-score-summary.no-growth-message',
  ClassAverageOverallMetricMessage = 'overall-score-summary.class-average-growth-metric-score-message',
  ClassAverageOverallMetricMOYMessage = 'overall-score-summary.class-average-growth-metric-score-moy-message',
  NoDistrictAverageScoreMessage = 'overall-score-summary.no-district-average-growth-metric-message',
  NoDistrictAverageScoreMOYMessage = 'overall-score-summary.no-district-average-growth-metric-moy-message',
  NoGrowthScoreMessage = 'overall-score-summary.no-growth-message',
  ClassAverageGrowthMetricPopoverMessage = 'overall-score-summary.class-average-growth-metric-score-card-popover-message',
}

export enum LineChartLabels {
  ChartLabels = 'line-chart',
  CustomBoxText = 'line-chart.custom-box-text',
  YAxisConfigs = 'y-axis-configs',
  XAxisConfigs = 'x-axis-configs',
  TooltipPointerTitles = 'line-chart.tooltip-pointer-titles',
}

export enum LineAreaChartLabels {
  ChartLabels = 'line-area-chart',
  CustomBoxText = 'line-area-chart.custom-box-text',
  YAxisConfigs = 'y-axis-configs',
  XAxisConfigs = 'x-axis-configs',
  TooltipPointerTitles = 'line-area-chart.tooltip-pointer-titles',
}

export interface GrowthScoreLabelItems {
  label: string;
  labelIconKey: string;
}
export interface DropdownMenuType {
  label: string;
  identifier: string;
}
export type AssessedStudentsPopoverItem = {
  studentName: string;
  studentId: string;
  score: string | number;
};

export interface GrowthReport {
  assessmentReportingType: string;
  assessedStudents: number;
  assignedStudents: number;
  classId: string;
  domains: string[];
  rootProgramId: string;
  placementLevelMinMaxValues: PlacementLevelMinMaxValues[];
  reports: GrowthReports[];
  students: Map<string, GrowthReportStudents>;
  districtId: string | null;
  classAverage: Map<string, number>;
  metricMeasureData: MetricMeasureData;
  metricsClassAverage: string | null;
}

export interface PlacementLevel {
  maximumValue: number;
  minimumValue: number;
  placementLevelCode: PlacementLevelCode | string;
}

export interface StudentsPerDomainGrowthPlacementLevel {
  placementLevelLabel: string;
  studentNumber: number;
  position: string;
}

export interface StudentReportRecord {
  userAssignmentId: string;
  studentId: string;
  studentName: string;
  assessmentReportingType?: AssessmentReportingType;
}

export type CachedStudentReportData = StudentReportData & {
  overallReportData: OverallReportData | null;
};

export type CachedStudentMultiStageReport = Omit<StudentMultiStageReport, 'studentReportData'> & {
  studentReportData: CachedStudentReportData[];
  assignmentId?: string;
};

export interface StudentReport extends StudentReportRecord {
  report?: CachedStudentMultiStageReport;
}

export interface StudentReportPayload {
  studentId?: string;
  studentName?: string;
  userAssignmentId?: string;
  report?: Report;
}

export interface MetricMeasureData {
  framework: FrameworkType;
  percentileScoreMapping: Record<string, string>;
}

export enum FrameworkType {
  LEXILE = 'lexile',
  QUANTILE = 'quantile',
}

export interface PercentileScoreMapping {
  assessmentReportingType: string;
  percentiles: Record<string, string>;
}

export interface DomainDropdownOptions {
  dropdownMenu: DropdownMenuType[];
  selectedOption: string;
}

export interface GrowthChartCheckboxSettings {
  showClassAverageSeries: boolean;
  showGrowthCustomBox: boolean;
}

export interface GrowthChartSettings extends GrowthChartCheckboxSettings {
  selectedDomain: string;
}

interface Report {
  classId: string;
  assignmentId?: string;
  studentsReportData: Student[] | null;
  overallReportData: OverallReportData | Record<string, never> | null;
}

interface Student extends CachedStudentReportData {
  firstName?: string;
  overallScaledScore?: number;
  nationalPercentile?: number;
  metrics?: Metrics;
}

export enum AssessmentReportingTypeLabels {
  BOY = 'Beginning of year',
  MOY = 'Middle of year',
  EOY = 'End of year',
}

export enum ReportingTypeLabels {
  BOY = 'reporting-type-labels.boy',
  MOY = 'reporting-type-labels.moy',
  EOY = 'reporting-type-labels.eoy',
}

export enum StudentGrowthCardLabels {
  ShowGrowthCardMessage = 'student-report.student-growth-score-card.student-growth-score-card-message',
  ShowGrowthCardZeroStateMessage = 'student-report.student-growth-score-card.student-growth-score-card-zero-state-message',
  ShowGrowthCardHeader = 'student-report.student-growth-score-card.student-growth-score-card-header',
  ShowGrowthCardMessageForOneDomainForSingleReportingType = 'student-report.student-growth-score-card.student-growth-score-card-message-for-single-reportingType',
}

export enum StudentMetricGrowthCardLabels {
  ShowGrowthMetricCardMessage = 'student-report.student-metric-growth-score-card.student-growth-score-card-message',
  ShowGrowthMetricCardZeroStateMessage = 'student-report.student-metric-growth-score-card.student-growth-score-card-zero-state-message',
  ShowGrowthMetricCardMessageForSingleReportingType = 'student-report.student-metric-growth-score-card.student-growth-score-card-message-for-single-reportingType',
  ShowGrowthMetricCardPopoverMessage = 'student-report.student-metric-growth-score-card.student-metric-growth-score-card-popover-message',
  ForecastingTitle = 'student-report.student-metric-growth-score-card.forecasting-card-title',
  ForecastingDynamicTextBeginning = 'student-report.student-metric-growth-score-card.forecasting-dynamic-beginning-text',
  ForecastingDynamicTextLikely = 'student-report.student-metric-growth-score-card.forecasting-dynamic-likely-text',
  ForecastingDynamicTextPerform = 'student-report.student-metric-growth-score-card.forecasting-dynamic-perform-text',
  ForecastingDynamicTextEnd = 'student-report.student-metric-growth-score-card.forecasting-dynamic-end-text',
  ForecastingLikelihoodLabel = 'student-report.student-metric-growth-score-card.forecasting-likelihood-label',
  ForecastingAssessmentWindowEndedMessage = 'student-report.student-metric-growth-score-card.forecasting-assessment-window-ended-message',
}

export enum StudentGrowthCardAccessibilityText {
  Description = 'student-report.student-growth-score-card.accessibility-text.description',
  StudentGrowthScoreAccessibilityText = 'student-report.student-growth-score-card.accessibility-text.student-growth-score',
  StudentMetricGrowthScoreAccessibilityText = 'student-report.student-growth-score-card.accessibility-text.student-metric-growth-score',
  ClassAverageGrowthScoreAccessibilityText = 'student-report.student-growth-score-card.accessibility-text.class-average-growth-score',
}

export enum OverallGrowthCardAccessibilityText {
  Description = 'overall-score-summary.accessibility-text.description',
  ClassAverageGrowthAccessibilityText = 'overall-score-summary.accessibility-text.class-overall-growth-score',
  ClassAverageGrowthMetricAccessibilityText = 'overall-score-summary.accessibility-text.class-overall-metric-growth-score',
  DistrictAverageAccessibilityText = 'overall-score-summary.accessibility-text.district-average-growth-score',
}

export interface LineChartData {
  id: string;
  lineChartItems: SavvasSharedUiLineChartItem[];
  xAxisConfigs: SavvasSharedUiLineChartXAxisOptions[];
  yAxisConfigs: SavvasSharedUiLineChartYAxisOptions[];
  toolTipConfigs: SavvasSharedUiLineChartToolTipOptions[];
  chartMargin: number[];
  chartStyleConfigs: SavvasSharedUiLineChartStyleConfigs;
}

export interface LineAreaChartData {
  id: string;
  lineChartItems: SavvasSharedUiLineAreaChartItem[];
  xAxisConfigs: SavvasSharedUiLineAreaChartXAxisOptions[];
  yAxisConfigs: SavvasSharedUiLineAreaChartYAxisOptions[];
  yAxisRange: AxisRange;
  toolTipConfigs: SavvasSharedUiLineAreaChartToolTipOptions[];
  chartMargin: number[];
  chartStyleConfigs: SavvasSharedUiLineChartStyleConfigs;
}

export interface CheckboxChangeEvent {
  checked: boolean;
  identifier: string;
}

export interface ColumnChartData {
  id: string;
  columnChartItems: SavvasSharedUiColumnChartItem[];
  xAxisConfigs: SavvasSharedUiColumnChartXAxisOptions[];
  yAxisConfigs: SavvasSharedUiColumnChartYAxisOptions;
  chartStyleConfigs: SavvasSharedUiColumnChartStyleConfigs;
}

export enum ColumnChartLabels {
  XAxisConfigs = 'column-chart.x-axis-configs',
  YAxisConfigs = 'column-chart.y-axis-configs',
  TooltipHeaderTitle = 'column-chart.tooltip-titles.header-title',
  TooltipPointerTitle = 'column-chart.tooltip-titles.pointer-title',
  SvgAltText = 'column-chart.svg-alt-text',
}

export enum ScreenSizeBreakPoint {
  RESOLUTION_1024 = 1024,
  SMALL_TABLET_WIDTH = 753,
}

export enum CelIconSizes {
  CUSTOM = 'custom',
  XSMALL = 'xsmall',
  XXSMALL = 'xxsmall',
  SMALL = 'small',
  REGULAR = 'regular',
  MEDIUM = 'medium',
  MLARGE = 'mlarge',
  LARGE = 'large',
  XLARGE = 'xlarge',
  XXLARGE = 'xxlarge',
  XXXLARGE = 'xxlarge',
}

export enum CelIconLabelType {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum CelIconColors {
  PRIMARY = 'primary',
  PRIMARY_SHADE = 'primary-shade',
  BLACK = 'black',
  WHITE = 'white',
  PLUM = 'plum',
  ERROR = 'error',
  ERROR_SHADE = 'error-shade',
  NOTIFICATION = 'notification',
  CONFIRM = 'confirm',
  DISABLED = 'disabled',
  GRAYDARKER = 'gray-darker',
  GRAYDUSTY = 'gray-dusty',
  GRAYLIGHT = 'gray-light',
  GRAYLIGHTER = 'gray-lighter',
  GRAY = 'gray',
  FEEDBACKGREEN = 'feedback-green',
}

export interface ForecastProbability {
  key: string;
  value: number;
}

export interface ForecastTextSegments {
  beginning: string;
  likely: string;
  perform: string;
  end: string;
}
